import { createWrapper } from "@segment/analytics-consent-tools";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { Account } from "@wingspanhq/wingspan-api-sdk";
import { CONSENT_CHANGED_EVENT } from "../../modules/CookieConsent/constants";
import { getCurrentPreferences } from "../../modules/CookieConsent/utils/consentManager";
import { getInvoiceIdFromCPEPath } from "../../modules/PaymentApp/utils";
import {
  BUILD_COMMIT,
  BUILD_TIMESTAMP
} from "../../shared/constants/buildInfo";
import { BUILD_ENV } from "../../shared/constants/environment";
import { localStorageUtil } from "../../utils/localStorage";
import { selectorAnalyticsAccountTraits } from "../selectors/selectorAnalyticsAccountTraits";
import { shouldSendAnalyticsCall } from "./analyticsGate";
import { intercomService } from "./intercomService";
import { postHogService } from "./postHogService";
import { tokenStore } from "./tokenStore";

const SEGMENT_CDN_URL = "https://analytics-cdn.wingspan.app";

export const SEGMENT_NOT_LOADED = "SEGMENT_NOT_LOADED";

export const getSegmentAnonymousIdFromLocalStorage = (): string => {
  const id =
    localStorageUtil.getItem("ajs_anonymous_id") || `"${SEGMENT_NOT_LOADED}"`;
  return id.split('"')[1];
};

export const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return "mobile";
  }
  return "desktop";
};

const buildEnv = BUILD_ENV;
const buildDate = new Date(BUILD_TIMESTAMP).toLocaleString(undefined, {
  hour12: false
});
const buildCommit = BUILD_COMMIT;

const getCategories = (): Record<string, boolean> => {
  const { C0001MT, C0002AL } = getCurrentPreferences();
  return { C0001MT, C0002AL };
};

class AnalyticsService {
  private client = new AnalyticsBrowser();

  // Opt-in model: device-mode destinations whose categories are not consented
  // are removed from the CDN settings before they load. Cloud-mode destinations
  // still receive events stamped with the consent context for server-side filtering.
  // Category → destination mapping is configured in the Segment workspace
  // (Privacy → Consent Management).
  private withConsent = createWrapper<AnalyticsBrowser>({
    getCategories,
    registerOnConsentChanged: setCategories => {
      document.addEventListener(CONSENT_CHANGED_EVENT, () => {
        setCategories(getCategories());
      });
    },
    shouldLoadSegment: ctx => ctx.load({ consentModel: "opt-in" })
  });

  get isLoaded(): boolean {
    return !!this.client.instance;
  }

  load(): void {
    if (this.isLoaded) return;

    try {
      const shouldSend = shouldSendAnalyticsCall();
      if (shouldSend && process.env.REACT_APP_SEGMENT_API_KEY) {
        this.withConsent(this.client).load(
          {
            cdnURL: SEGMENT_CDN_URL,
            writeKey: process.env.REACT_APP_SEGMENT_API_KEY
          },
          { initialPageview: false }
        );

        this.page();
      }
    } catch (error) {
      console.error("Failed loading Segment.io", error);
    }
  }

  page(name: string | undefined = undefined, properties: any = {}): void {
    const customProperties = {
      invoiceId: getInvoiceIdFromCPEPath(window.location.pathname)
    };
    const eventName = name;
    const eventProperties = {
      ...properties,
      ...customProperties
    };
    const eventOptions = this.getIntercomIntegrationOptions();

    if (shouldSendAnalyticsCall()) {
      this.client.page(eventName, eventProperties, eventOptions);
    }
  }

  identify(accountId: string | undefined = undefined, traits: any = {}): void {
    try {
      const eventTraits = { ...traits };
      const eventOptions = {
        ...this.getCommonFields(),
        ...this.getIntercomIntegrationOptions()
      };

      if (shouldSendAnalyticsCall()) {
        this.client.identify(accountId, eventTraits, eventOptions);
        postHogService.identify(accountId);
      } else {
        postHogService.reset();
      }
    } catch (error) {
      console.error("IDENTIFY ERROR:", error);
    }
  }

  identifyAccount(account: Account): void {
    intercomService.shutdown();

    this.identify(
      account.id ?? undefined,
      selectorAnalyticsAccountTraits(account)
    );
  }

  track(event: string | undefined = undefined, properties: any = {}): void {
    if (shouldSendAnalyticsCall() && event) {
      this.client.track(event, { ...properties });
    }
  }

  reset(): void {
    this.client.reset();
    postHogService.reset();
    // Intercom is a global widget booted by Segment's device-mode destination
    // and isn't torn down by the Segment reset. Without this the previously
    // authenticated Intercom session (and its conversation history) stays
    // booted into the next screen, because logout and impersonation switches
    // are in-SPA navigations.
    intercomService.shutdown();
  }

  group(groupId: string, traits?: any): void {
    if (shouldSendAnalyticsCall()) {
      this.client.group(groupId, traits);
    }
  }

  private getCommonFields() {
    const deviceType = getDeviceType();
    const accountId = tokenStore.getIdentity()?.accountId;

    return {
      ...(accountId ? { userId: accountId } : {}),
      context: {
        wingspan: {
          buildCommit,
          buildDate,
          buildEnv,
          deviceType
        }
      }
    };
  }

  private getIntercomIntegrationOptions() {
    if (window.intercomUserJwt) {
      return {
        integrations: {
          Intercom: { intercom_user_jwt: window.intercomUserJwt }
        }
      };
    }

    if (window.intercomUserHash) {
      return {
        integrations: { Intercom: { user_hash: window.intercomUserHash } }
      };
    }

    return {};
  }
}

export const analyticsService = new AnalyticsService();
